import axios from 'axios';
import moment from "moment";
import { useEffect } from 'react';
import { useMemo } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useSelector, useDispatch } from "react-redux";

// export function useGetTeams(token) {
//   return useQuery('teams', async () => {
//     const response = await axios.get('/api/team', {
//       headers: {
//         Authorization: `Bearer ${token}`,
//       },
//     });
//     const sortedTeams = response.data.sort((a, b) => {
//       const nameA = a.first_name ? `${a.first_name} ${a.middle_name ? a.middle_name : ''} ${a.last_name}` : `${a.company_first_name ? a.company_first_name + (a.company_last_name && a.company_last_name) : a.company_name}`;
//       const nameB = b.first_name ? `${b.first_name} ${b.middle_name ? b.middle_name : ''} ${b.last_name}` : `${b.company_first_name ? b.company_first_name + (b.company_last_name && b.company_last_name) : b.company_name}`;
//       return nameA.localeCompare(nameB);
//     });
//     return sortedTeams;
//   });
// }

// export function useGetClients(token) {
//   return useQuery('clients', async () => {
//     const response = await axios.get('/api/client-details', {
//       headers: {
//         Authorization: `Bearer ${token}`,
//       },
//     });
//     return response.data;
//   });
// }


// export function useGetDepartments() {
//   return useQuery('departments', async () => {
//     const response = await axios.get('/api/user-department');
//     return response.data;
//   });
// }

export function useExpenseId(token) {
  return useQuery(['expenseId', token], async () => {
    const response = await axios.get('/api/expense/id', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }, { enabled: !!token, staleTime: 5 * 60 * 1000, cacheTime: 10 * 60 * 1000, refetchOnWindowFocus: false });
}

export function useGetExpenseGraphData(token, expense, date, currency, allCurrency, defaultCurrency, forex, tz) {
  return useQuery(['expenseGraphData', token, expense, date, currency, allCurrency], async () => {
    const timeZoneDate = date && moment(date).tz(tz).format();
    const response = await axios.post(
      '/api/expense-data/chart',
      {
        date: timeZoneDate ? timeZoneDate : moment(new Date()).tz(tz).format(),
        team: expense.expTeam,
        client: expense.expClient,
        department: expense.expDepartment,
        category: expense.filterExpCategory,
        currency: currency,
        allCurrency: allCurrency,
        defaultCurrency,
        forex,
        tz
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    const processedData = response.data.map(item => ({
      ...item,
      exps: parseFloat(item.exps).toFixed(3),
      day: moment(item.key, "YYYY-MM-DD").format("ddd, MMM D")
    }));
    return processedData;
  }, { enabled: !!token });
}


export function useGetExpenseData(token, expense, startDate, endDate, page = 1, limit = 10, allCurrency = true, selectedCurrency = null) {
  const startDateStr = startDate ? moment(startDate).format('YYYY-MM-DD') : null;
  const endDateStr = endDate ? moment(endDate).format('YYYY-MM-DD') : null;

  const currencies = useMemo(() => {
    if (expense.filterExpCurrency && expense.filterExpCurrency.length > 0) return expense.filterExpCurrency;
    return [];
  }, [expense.filterExpCurrency]);

  const notification = useSelector((state) => state.notification.notificationState);

  const { data: rawData, isLoading, refetch } = useQuery(
    [
      'expenseData',
      token,
      startDateStr,
      endDateStr,
      expense.expTeam,
      expense.expClient,
      expense.expDepartment,
      expense.filterExpCategory,
      currencies,
      expense.filterExpStatus,
      page,
      limit,
    ],
    async () => {
      const result = await axios.post(
        '/api/expenses/data-optimize',
        {
          startDate: startDateStr ? moment.utc(startDateStr, "YYYY-MM-DD").startOf("day").toDate() : null,
          endDate: endDateStr ? moment.utc(endDateStr, "YYYY-MM-DD").endOf("day").toDate() : null,
          tz: global.config.getTimeZone(),
          teams: expense.expTeam || [],
          clients: expense.expClient || [],
          departments: expense.expDepartment || [],
          expenseCategories: expense.filterExpCategory || [],
          expenseCurrencies: currencies,
          statuses: expense.filterExpStatus || [],
          page,
          limit,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return result.data;
    },
    { enabled: !!token }
  );

  const queryClient = useQueryClient();
  useEffect(() => {
    refetch();
    if (notification) {
      setTimeout(() => {
        queryClient.invalidateQueries('expenseCurrency');
      }, 500); // Small delay to ensure DB write is fully committed before fetching
    }
  }, [notification]);

  return {
    data: rawData?.data || [],
    totalCount: rawData?.totalCount || 0,
    totalPages: rawData?.totalPages || 0,
    currencyTotals: rawData?.currencyTotals || [],
    isLoading,
    refetch,
  };
}

export function useGetClientExpenseData(token, clientId) {
  return useQuery(['clientExpenseData', clientId], async () => {
    const response = await axios.post('/api/expenses/client/data', {
      clientId: clientId,
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }, {
    staleTime: 3 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export async function getBankDetailsByAppOwner(appId, wxComapny, token) {
  const requestData = { appId, wxComapny };

  try {
    const response = await axios.post('/api/app-owner-bank/by-appowner/get', requestData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.status === 200) {
      return response.data.data;
    } else {
      throw new Error(`Unexpected response status: ${response.status}`);
    }

  } catch (error) {
    console.error("Error fetching bank details by App Owner:", error);
    if (error.response) {
      const { status, data } = error.response;
      console.error(`Response error: ${status} - ${data?.message || data}`);
      throw new Error(`Server responded with status ${status}: ${data?.message || "Unknown error"}`);
    } else if (error.request) {
      console.error("No response received:", error.request);
      throw new Error("No response received from the server. Please check your network connection.");
    } else {
      console.error("Error in request setup:", error.message);
      throw new Error(`Error in request setup: ${error.message}`);
    }
  }
}

export const useTotalBudgetOfExpense = (expenseCategories, token, forex, currency) => {

  return useQuery(['totalBudgetOfExpense', token, expenseCategories, forex, currency], async () => {
    try {
      const requestData = { expenseCategories, forex, currency };

      const response = await axios.post('/api/expenses/total-budget', requestData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data.totalBudget;
    } catch (error) {
      global.config.slackMessage(error.toString());
      throw new Error('Error fetching total budget');
    }
  }, { enabled: !!token });
};
export function useFetchExpenseCurrency(token, expense, startDate, endDate) {
  const startDateStr = startDate ? moment(startDate).format('YYYY-MM-DD') : null;
  const endDateStr = endDate ? moment(endDate).format('YYYY-MM-DD') : null;

  return useQuery([
    'expenseCurrency', 
    token, 
    startDateStr,
    endDateStr,
    expense?.expTeam,
    expense?.expClient,
    expense?.expDepartment,
    expense?.filterExpCategory,
    expense?.filterExpStatus,
  ], async () => {
    const response = await axios.post('/api/expenses/currency', {
      startDate: startDateStr ? moment.utc(startDateStr, "YYYY-MM-DD").startOf("day").toDate() : null,
      endDate: endDateStr ? moment.utc(endDateStr, "YYYY-MM-DD").endOf("day").toDate() : null,
      tz: moment.tz.guess(),
      teams: expense?.expTeam || [],
      clients: expense?.expClient || [],
      departments: expense?.expDepartment || [],
      expenseCategories: expense?.filterExpCategory || [],
      statuses: expense?.filterExpStatus || [],
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }, { staleTime: 5 * 60 * 1000, cacheTime: 10 * 60 * 1000, refetchOnWindowFocus: false });
}
